import React, { useEffect, useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  ExitToApp as LogoutIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

const LogoutScreen: React.FC = () => {
  useDocumentTitle('Logout');
  const [countdown, setCountdown] = useState(3);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { setToken, setUser } = useAuth();

  useEffect(() => {
    // Perform logout when component mounts
    const performLogout = async () => {
      try {
        // Call the API logout directly
        await apiService.logout();
        
        // Clear the auth context state using setTimeout to defer until after render
        setTimeout(() => {
          setToken(null);
          setUser(null);
        }, 0);
      } catch (err) {
        console.error('Logout error in LogoutScreen:', err);
        // Even if logout fails, we should still clear local storage
        // and continue with the logout flow
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        setTimeout(() => {
          setToken(null);
          setUser(null);
        }, 0);
      }
    };

    performLogout();

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate('/login', { replace: true });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate, setToken, setUser]);

  return (
    <Container 
      component="main" 
      maxWidth="sm"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        py: 4,
      }}
    >
      <Paper
        elevation={8}
        sx={{
          padding: isMobile ? 3 : 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          maxWidth: 400,
          textAlign: 'center',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <CheckCircleIcon
            sx={{
              fontSize: 80,
              color: theme.palette.success.main,
              mb: 2,
            }}
          />
          
          <Typography
            component="h1"
            variant="h4"
            sx={{
              fontWeight: 600,
              color: theme.palette.primary.main,
              mb: 1,
            }}
          >
            Successfully Logged Out
          </Typography>
          
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.text.secondary,
              mb: 3,
            }}
          >
            You have been securely logged out of your account.
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <LogoutIcon
              sx={{
                fontSize: 20,
                color: theme.palette.text.secondary,
              }}
            />
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
              }}
            >
              Redirecting to login page in {countdown} second{countdown !== 1 ? 's' : ''}...
            </Typography>
          </Box>
          
          <CircularProgress
            size={24}
            sx={{
              color: theme.palette.primary.main,
            }}
          />
        </Box>
      </Paper>
    </Container>
  );
};

export default LogoutScreen;
